import * as React from "react";
import { useForm } from "react-hook-form";
import { Select } from "@jhool-io/fe-components";
import { useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "../../../../hooks/helpers";
import useToast from "../../../../hooks/useToast";
import { ComboOption } from "../../../../utils/types";
import { useFetchProviders } from "../../../providers/hooks/providers.queries";
import { useCreateNoteRuleBypass } from "../../hooks/note-rule-bypass.mutations";
import { useFetchClientsForNoteRuleBypass } from "../../hooks/note-rule-bypass.queries";
import { NoteRule } from "../../types/note-rule-bypass.types";

interface AddNoteRuleBypassFormProps {
    rule: NoteRule;
    onFormSubmit: () => void;
}

export default function AddNoteRuleBypassForm({
    rule,
    onFormSubmit,
}: AddNoteRuleBypassFormProps) {
    const [providerSearch, setProviderSearch] = React.useState("");
    const [clientSearch, setClientSearch] = React.useState("");
    const [selectedProvider, setSelectedProvider] =
        React.useState<ComboOption | null>(null);
    const [selectedClient, setSelectedClient] =
        React.useState<ComboOption | null>(null);

    const debouncedProviderSearch = useDebounce(providerSearch, 500);
    const debouncedClientSearch = useDebounce(clientSearch, 500);

    const { handleSubmit } = useForm();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const createBypass = useCreateNoteRuleBypass();

    const providersQuery = useFetchProviders({
        page: 1,
        limit: 20,
        search_string: debouncedProviderSearch,
    });

    const clientsQuery = useFetchClientsForNoteRuleBypass(
        {
            provider_id: selectedProvider?.value ?? "",
            rule,
            search: debouncedClientSearch || undefined,
            limit: 20,
        },
        Boolean(selectedProvider)
    );

    const providerOptions: ComboOption[] =
        providersQuery.data?.data.map((provider) => ({
            label: provider.provider_name,
            value: provider.provider_id,
        })) ?? [];

    const clientOptions: ComboOption[] =
        clientsQuery.data?.data.clients.map((client) => ({
            label: client.client_name,
            value: client.client_id,
        })) ?? [];

    const onSubmit = () => {
        if (!selectedProvider) {
            toast({ mode: "error", message: "Provider field cannot be empty" });
            return;
        }
        if (!selectedClient) {
            toast({ mode: "error", message: "Client field cannot be empty" });
            return;
        }

        createBypass.mutate(
            {
                rule,
                provider_id: selectedProvider.value,
                client_id: selectedClient.value,
            },
            {
                onSuccess: (res) => {
                    toast({
                        mode: "success",
                        message:
                            res.message ||
                            "Note rule override added successfully",
                    });
                    queryClient.invalidateQueries({
                        queryKey: ["note-rule-bypasses"],
                    });
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Error adding note rule override",
                    });
                },
            }
        );
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            id="add-note-rule-bypass"
            className="flex flex-col gap-[--spacing-items]"
        >
            <div className="fg">
                <Select
                    label="Provider"
                    isRequired
                    isSearchable
                    isLongListInDialog
                    isLoading={providersQuery.isLoading}
                    options={providerOptions}
                    value={selectedProvider}
                    onInputChange={(value) => setProviderSearch(value)}
                    filterOption={() => true}
                    onChange={(val) => {
                        const option = val as ComboOption | null;
                        setSelectedProvider(option);
                        setSelectedClient(null);
                        setClientSearch("");
                    }}
                    placeholder={
                        providersQuery.error
                            ? "Error loading providers"
                            : "Select a provider"
                    }
                />
            </div>

            {selectedProvider && (
                <div className="fg">
                    <Select
                        label="Client"
                        isRequired
                        isSearchable
                        isLongListInDialog
                        isLoading={clientsQuery.isLoading}
                        options={clientOptions}
                        value={selectedClient}
                        onInputChange={(value) => setClientSearch(value)}
                        filterOption={() => true}
                        onChange={(val) =>
                            setSelectedClient(val as ComboOption | null)
                        }
                        placeholder={
                            clientsQuery.error
                                ? "Error loading clients"
                                : "Select a client"
                        }
                        noOptionsMessage={() =>
                            "This provider has no eligible clients for this rule"
                        }
                    />
                </div>
            )}
        </form>
    );
}
